/**
 * SIMS-9031 — the confirmation shown before a disenrollment-threshold change is applied.
 *
 * This is not UI polish. Decision D1 accepted that lowering a threshold disenrolls participants for
 * absence they had already accrued; D2 made this confirmation the control that makes that
 * acceptable. Its copy is a compliance artifact (CR-5), so the rules live here with tests rather
 * than inline in the component.
 */

export interface PropagationForecast {
	/** ACTIVE and PAUSED, including non-weekly plans (D5). */
	toUpdate: number;
	/** Holding a threshold set specifically for them — preserved unless explicitly included. */
	customised: number;
	/** ACTIVE and weekly-periodicity only (D5) — the disenrollment check's own gate. */
	forecastDisenrollments: number;
}

/**
 * Is the administrator allowed to proceed? (AC2, AC4)
 *
 * When the change would disenroll nobody there is no number to acknowledge and the button is
 * simply enabled (AC4). When it would, the typed value must EXACTLY equal the forecast — not
 * merely be non-empty — because the point of the interaction is that the administrator has read
 * and understood that specific number.
 */
export const canProceed = (
	forecast: PropagationForecast,
	typed: string,
): boolean => {
	if (forecast.forecastDisenrollments === 0) {
		return true;
	}
	return typed.trim() === String(forecast.forecastDisenrollments);
};

/** Does this change require the administrator to type the number back? (AC2/AC4) */
export const requiresAcknowledgment = (
	forecast: PropagationForecast,
): boolean => forecast.forecastDisenrollments > 0;

/**
 * The sentence stating what will happen to participants (AC3, AC4, AC5).
 *
 * Deliberately worded as a FORECAST for the next automatic check rather than a promise: the counts
 * are computed now and the job runs at 06:00 UTC, so a participant who attends before then drops
 * out of it. CR-5 requires this to survive review, so the wording is asserted by tests.
 */
export const disenrollmentSentence = (
	forecast: PropagationForecast,
): string => {
	if (forecast.forecastDisenrollments === 0) {
		return "No participants will be automatically disenrolled by this change.";
	}

	const plural = forecast.forecastDisenrollments === 1 ? "" : "s";
	return (
		`${forecast.forecastDisenrollments} participant${plural} will be automatically disenrolled ` +
		"at the next automatic check. This figure may change if participants attend before then."
	);
};

/**
 * How the write population is described (FR-11).
 *
 * The two counts span different populations by design (D5) — more participants are written than
 * can be disenrolled, because paused and non-weekly participants are updated but never checked.
 * The wording has to make that read as intentional rather than inconsistent.
 */
export const updateSentence = (forecast: PropagationForecast): string => {
	const plural = forecast.toUpdate === 1 ? "" : "s";
	return `${forecast.toUpdate} participant${plural} will be updated, including anyone whose enrollment is paused.`;
};

/**
 * How participant-specific thresholds are described (SIMS-9030 AC2).
 *
 * `customised` counts who HOLDS their own threshold, which does not change when the administrator
 * opts to include them — so the outcome clause has to come from `includeCustomised`, not from the
 * count. Without that, ticking the include box left this sentence asserting "will be left as they
 * are" about the very participants the line above says will be updated: two parts of the same
 * confirmation contradicting each other, which is what D2's acknowledgment cannot survive.
 */
export const customisedSentence = (
	forecast: PropagationForecast,
	includeCustomised = false,
): string | null => {
	if (forecast.customised === 0) {
		return null;
	}
	const plural = forecast.customised === 1 ? "" : "s";
	const verb = forecast.customised === 1 ? "holds" : "hold";
	const outcome = includeCustomised
		? "will be updated with everyone else"
		: "will be left as they are";
	return `${forecast.customised} participant${plural} ${verb} a threshold set specifically for them and ${outcome}.`;
};
